// src/lib/menage-status-helpers.js — PR #151 PARTIE D (ADR-084)
// + AMENDEMENT (ADR-086) : helper resolveLogTime + verrouillage check-out time.
//
// Helper pur pour calculer le statut "effectif" d'un menage_run en
// combinant `status` (DB) + `scheduled_date` vs `today` (Paris ISO).
//
// Problème : la table `menage_runs.status` ne capture pas la dimension
// temporelle. Un run `status='in_progress'` daté du J-2 reste affiché
// "🚧 En cours" alors qu'il est en réalité oublié/passé. Pire, un run
// futur (J+5) avec status='scheduled' s'affiche identique à celui du
// jour, sans distinction visuelle (anti-anticipation cassée).
//
// AMENDEMENT P3 : extension du verrouillage à l'heure du check-out
// du voyageur précédent. Un run sur le jour J avec checkout_time=11:00
// reste "📆 Prévu" jusqu'à 11:00 puis devient "📅 À faire" / "🧹 En cours".
//
// Solution : un helper qui arbitre dans cet ORDRE STRICT :
//   1. completed mais date > today → ANOMALIE (T-2 Dimitri Option B)
//   2. completed → ✅ Terminé
//   3. skipped → ⏭ Passé skipped
//   4. date < today → ⏭️ Passé (date domine status — Q2 Dimitri)
//   5. date > today → 📆 Prévu (anti-anticipation, peu importe status)
//   6. date == today + nowHHMM < activationHHMM → 📆 Prévu (verrouillé)
//   7. date == today + status='in_progress' → 🧹 En cours
//   8. date == today + status='scheduled' → 📅 À faire
//
// Usage : pure function, pas de side-effect (Sentry log à faire dans
// le caller via useEffect si key === 'anomalie'). Comparaisons string
// ISO YYYY-MM-DD valides (`<`, `>`, `===`).
//
// EFFECTIVE_STATUS_KEYS : ordre stable pour la légende UI.

const STATUS_VISUALS = {
  scheduled:       { label: '📅 À faire',         bg: '#dbeafe', fg: '#1e40af' },
  in_progress:     { label: '🧹 En cours',        bg: '#fef3c7', fg: '#92400e' },
  upcoming:        { label: '📆 Prévu',           bg: '#f1f5f9', fg: '#475569' },
  completed:       { label: '✅ Terminé',         bg: '#d1fae5', fg: '#065f46' },
  past:            { label: '⏭️ Passé',           bg: '#e5e7eb', fg: '#4b5563' },
  skipped:         { label: '⏭ Passé skipped',    bg: '#e5e7eb', fg: '#4b5563' },
  // ADR-207 — au-delà de 48h le run est FERMÉ. « fait » (completed_at) = neutre ;
  // « non saisi » = orange pour aider l'admin à repérer les trous de saisie.
  ferme_fait:      { label: '🔒 Fermé · fait',      bg: '#e2e8f0', fg: '#475569' },
  ferme_non_saisi: { label: '🔒 Fermé · non saisi', bg: '#ffedd5', fg: '#9a3412' },
  anomalie:        { label: '🚨 Anomalie',        bg: '#fef3c7', fg: '#b91c1c' },
}

/**
 * Ordre stable d'affichage de la légende dans MenageCalendrierView.
 * Ne pas réordonner sans aligner la légende UI.
 *
 * N5 : `anomalie` est VOLONTAIREMENT absent de cette légende — c'est un état de
 * détection interne (run completed daté dans le futur) surfacé côté ADMIN
 * (pastille RunCard + breadcrumb Sentry), pas un statut à documenter au ménage/
 * orchestrateur dans la vue Mois. `getEffectiveRunStatus` continue de le
 * retourner (lecture directe de STATUS_VISUALS.anomalie) → la détection reste
 * intacte ; seule la LÉGENDE ne le liste plus.
 */
export const EFFECTIVE_STATUS_KEYS = [
  'scheduled',
  'in_progress',
  'upcoming',
  'completed',
  'past',
  'ferme_fait',
  'ferme_non_saisi',
]

/**
 * Retourne le visuel { label, bg, fg } pour une key donnée
 * (utilisé par la légende — pas de logique de date).
 *
 * @param {string} key - parmi EFFECTIVE_STATUS_KEYS (+ 'skipped' implicite)
 * @returns {{ label: string, bg: string, fg: string }}
 */
export function getStatusVisual(key) {
  return STATUS_VISUALS[key] || STATUS_VISUALS.scheduled
}

// ── Parser format libre "11h"/"11h30"/"11:00"/"11" → "HH:MM" ─────────
//
// Accepte : "11h", "11h30", "11h00", "11:00", "11.30", "11", "9h"
// Refuse  : "abc", "25:00", "11:60", "", null, undefined
// Sortie  : padded "HH:MM" ou null
export function parseHourString(s) {
  if (!s || typeof s !== 'string') return null
  const trimmed = s.trim().toLowerCase()
  // "11h30" / "11h" / "11:30" / "11.30" / "11"
  const m = trimmed.match(/^(\d{1,2})[h:.]?(\d{0,2})$/)
  if (!m) return null
  const h = parseInt(m[1], 10)
  const min = m[2] ? parseInt(m[2], 10) : 0
  if (Number.isNaN(h) || Number.isNaN(min)) return null
  if (h < 0 || h > 23 || min < 0 || min > 59) return null
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`
}

/**
 * Résout l'heure check-in ou check-out d'un logement (self) + parsing format
 * libre "11h"/"11h30" → "HH:MM".
 *
 * @param {Object} log - logement courant (peut être null)
 * @param {Object|null} _parentLog - conservé pour compat, non utilisé (ADR-197)
 * @param {'checkin'|'checkout'} kind
 * @returns {string|null} format "HH:MM" ou null si non trouvé
 *
 * ADR-197 (LOT 1) : le fallback parent (chambres sous-loc) a été retiré — les
 * chambres ont leur propre schedule checkin/checkout seedé (LOT 0). Twin
 * symétrique de src/lib/template-variables.js + lib/template-variables.js.
 *
 * Stratégie de résolution (LOT E, FLIP « Instructions-first », décision Dimitri) :
 *   1. log[kindField] (champ Instructions = vérité ADR-199 ; "11h"/"11h30"/"11:00")
 *   2. log.schedule[scheduleKey] (checkin_start ou checkout_time) — fallback
 *   3. null
 * lc1/lc2/lc3 → 16h→17h (checkin_time prime) ; checkout concordant (no-op) ;
 * sans checkin_time → fallback schedule (sûr). parseHourString gère 2 formats.
 *
 * scheduleKey : 'checkin' → 'checkin_start', 'checkout' → 'checkout_time'
 * kindField   : 'checkin' → 'checkin_time', 'checkout' → 'checkout_time'
 */
export function resolveLogTime(log, _parentLog, kind) {
  if (kind !== 'checkin' && kind !== 'checkout') return null
  const scheduleKey = kind === 'checkin' ? 'checkin_start' : 'checkout_time'
  const kindField   = kind === 'checkin' ? 'checkin_time'  : 'checkout_time'

  const tryOne = (l) => {
    if (!l) return null
    // Priorité 1 (LOT E) : champ libre Instructions ("11h", "11:00", etc.) = vérité
    const parsedField = parseHourString(l[kindField])
    if (parsedField) return parsedField
    // Priorité 2 : schedule[scheduleKey] (fallback, déjà au format HH:MM côté config)
    const sched = l.schedule?.[scheduleKey]
    const parsedSched = parseHourString(sched)
    if (parsedSched) return parsedSched
    return null
  }

  return tryOne(log)
}

/**
 * Retourne { key, label, bg, fg, activationHHMM? } selon l'arbitrage
 * documenté en tête de fichier. Pure function (pas de side-effect).
 *
 * @param {{ status: string, scheduled_date: string }} run - Run DB. `scheduled_date` au format ISO YYYY-MM-DD.
 * @param {string} todayIso - Date du jour Europe/Paris au format ISO YYYY-MM-DD.
 * @param {string|null} [nowHHMM] - Heure courante Paris "HH:MM" (utilisé seulement si scheduled_date === today).
 *                                  Si null/invalide → assumé "23:59" (run actif, sécurité).
 * @param {string|null} [checkoutTime] - log.checkout_time résolu via resolveLogTime ("HH:MM").
 *                                       Si null → activation "00:00" (premier voyageur, accès dès minuit).
 * @returns {{ key: string, label: string, bg: string, fg: string, activationHHMM?: string }}
 */
export function getEffectiveRunStatus(run, todayIso, nowHHMM, checkoutTime) {
  const status = run?.status || 'scheduled'
  const sd = run?.scheduled_date || ''

  // 1. Run "completed" mais date > today → anomalie (run terminé en
  //    avance, vérifier — fraude possible ou erreur saisie).
  if (status === 'completed' && sd > todayIso) {
    return { key: 'anomalie', ...STATUS_VISUALS.anomalie }
  }
  // 2. Skipped explicite (admin a marqué passé) — distinct, prioritaire.
  if (status === 'skipped') {
    return { key: 'skipped', ...STATUS_VISUALS.skipped }
  }
  // 3. Verrou 48h (ADR-207) : au-delà de scheduled_date + 48h, le run est
  //    FERMÉ. On distingue « fait » (completed_at présent) vs « non saisi »
  //    (jamais validé) pour que l'admin repère les trous. Prioritaire sur
  //    Terminé/Passé (un completed >48h devient « Fermé · fait »).
  if (sd && isMenageRunLockedForMenage(sd, todayIso)) {
    if (status === 'completed' || run?.completed_at) {
      return { key: 'ferme_fait', ...STATUS_VISUALS.ferme_fait }
    }
    return { key: 'ferme_non_saisi', ...STATUS_VISUALS.ferme_non_saisi }
  }
  // 4. Cas nominal terminé (≤48h).
  if (status === 'completed') {
    return { key: 'completed', ...STATUS_VISUALS.completed }
  }
  // 5. Date passée (≤48h, ex. hier) → "Passé" (la date domine le status — un
  //    in_progress oublié sur du J-1 doit afficher "Passé", pas "En cours").
  if (sd < todayIso) {
    return { key: 'past', ...STATUS_VISUALS.past }
  }
  // 5. Date future → "Prévu" (anti-anticipation, masque la nuance
  //    scheduled/in_progress qui n'a pas de sens avant le jour J).
  if (sd > todayIso) {
    return { key: 'upcoming', ...STATUS_VISUALS.upcoming }
  }
  // 6. Date == today + heure courante avant l'activation → verrouillé.
  //    activationHHMM = checkoutTime || "00:00" (fallback minuit si pas
  //    de check-out config, ex premier voyageur ou logement neuf).
  //    nowHHMM null/invalide → "23:59" (sécurité, on déverrouille).
  const activationHHMM = parseHourString(checkoutTime) || '00:00'
  const safeNow = parseHourString(nowHHMM) || '23:59'
  if (safeNow < activationHHMM) {
    return { key: 'upcoming', ...STATUS_VISUALS.upcoming, activationHHMM }
  }
  // 7. Date == today, in_progress.
  if (status === 'in_progress') {
    return { key: 'in_progress', ...STATUS_VISUALS.in_progress, activationHHMM }
  }
  // 8. Date == today, scheduled (cas nominal "à faire aujourd'hui").
  return { key: 'scheduled', ...STATUS_VISUALS.scheduled, activationHHMM }
}

// ── Verrou temporel 48h (ADR-207) ────────────────────────────────────
//
// Le rôle MÉNAGE ne peut plus éditer un run à partir de `scheduled_date + 48h`
// (= minuit Paris du surlendemain). Admin exempté (policy `admin_all`
// inconditionnelle). Ces helpers sont le MIROIR de la RLS `menage_update_own`
// (migration 20260618120000) : éditable SSI `now_paris < scheduled_date +
// interval '2 days'`.
//
// Équivalence DATE (point clé) : le seuil RLS est à MINUIT (Paris) de J+2, donc
// `now_paris < minuit(J+2)` ⟺ `date_paris_du_jour < J+2` ⟺ `todayIso <
// scheduled_date + 2`. La comparaison se ramène donc à une comparaison de DATES
// ISO (string `<`/`>=` valides) — pas besoin de l'heure. On passe `todayIso`
// (date Paris du jour, `todayParisIso()`), ce qui rend le verrou DÉTERMINISTE et
// testable (même source que le reste de l'UI). Pas de drift DST à ce grain (le
// seuil est toujours « minuit Paris de J+2 »).

// Date ISO du verrou : à partir de ce jour (INCLUS), le run est fermé pour le
// ménage. = scheduled_date + 2 jours. Arithmétique en UTC (composantes pures de
// date, sans heure → indépendante du fuseau machine).
export function menageRunLockDateIso(scheduledDate) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(scheduledDate || ''))) return null
  const [y, m, d] = scheduledDate.split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d))
  dt.setUTCDate(dt.getUTCDate() + 2)
  const p = (n) => String(n).padStart(2, '0')
  return `${dt.getUTCFullYear()}-${p(dt.getUTCMonth() + 1)}-${p(dt.getUTCDate())}`
}

/**
 * Le run est-il FERMÉ pour le rôle MÉNAGE à la date Paris `todayIso` ?
 * Fermé SSI `todayIso >= scheduled_date + 2` (comparaison string ISO valide).
 * Fail-safe : `scheduled_date` invalide/absent → NON fermé côté UX (on ne
 * verrouille pas une donnée douteuse ; la RLS reste l'arbitre serveur).
 *
 * @param {string} scheduledDate - "YYYY-MM-DD"
 * @param {string} todayIso - date Paris du jour "YYYY-MM-DD" (todayParisIso())
 * @returns {boolean}
 */
export function isMenageRunLockedForMenage(scheduledDate, todayIso) {
  const lockDate = menageRunLockDateIso(scheduledDate)
  if (!lockDate) return false
  return String(todayIso || '') >= lockDate
}

/** Inverse : le rôle MÉNAGE peut-il encore éditer ce run à `todayIso` ? */
export function isMenageRunEditableByMenage(scheduledDate, todayIso) {
  return !isMenageRunLockedForMenage(scheduledDate, todayIso)
}

// ── Ouverture jour-J pour le rôle MÉNAGE (ADR-230, Chantier 42 Phase 3) ──
//
// DÉCISION DIMITRI : l'agent ménage peut valider la checklist + déclarer un
// incident dès 00:01 le JOUR J (scheduled_date === todayIso), QUELLE QUE SOIT
// l'heure — fini le verrou « heure de check-out » bloquant. Le créneau
// théorique devient un bandeau purement informatif (non bloquant) côté modal.
//
// Bornes :
//   - jour J STRICT (sd === todayIso) : ouvert (sous réserve du verrou 48h).
//   - jour futur (sd > today) : FERMÉ (anti-anticipation — égalité stricte).
//   - jour passé (sd < today) : FERMÉ ; la borne 48h (isMenageRunEditableByMenage)
//     gère le cas « hier ≤ 48h » côté RLS/UX legacy, mais ici on ne ré-ouvre PAS
//     un run d'un jour ≠ aujourd'hui (l'égalité stricte prime ; un run de la
//     veille n'est plus « du jour »). La fenêtre incident 48h reste gérée
//     séparément par isWithinIncidentWindow (run completed récent).
//
// On réutilise isMenageRunEditableByMenage UNIQUEMENT pour la borne 48h (pas de
// duplication de la logique de verrou).

/**
 * Le run est-il OUVERT à l'édition pour le rôle MÉNAGE à la date Paris `todayIso` ?
 * Ouvert SSI scheduled_date === todayIso (jour J STRICT) ET pas verrouillé 48h.
 *
 * @param {{ scheduled_date: string }} run - Run DB.
 * @param {string} todayIso - date Paris du jour "YYYY-MM-DD" (todayParisIso()).
 * @returns {boolean}
 */
export function isRunOpenForMenageToday(run, todayIso) {
  const sd = run?.scheduled_date
  if (!sd || !todayIso) return false
  // Le ménage édite un run UNIQUEMENT le jour J (dès 00:01, quelle que soit
  // l'heure), et tant qu'il n'est pas verrouillé 48h. Jour futur / passé → fermé
  // (le verrou 48h gère le passé via isMenageRunEditableByMenage).
  return sd === todayIso && isMenageRunEditableByMenage(sd, todayIso)
}
